import React, { useState, useEffect } from 'react';
import { X, Check, XCircle, Loader2 } from 'lucide-react';
import {
  fetchComparisonSuggestions,
  createComparisonSession,
  updateComparisonSession,
  fetchContracts,
  type FinancialRiskFinding,
  type ComparisonSuggestion,
  type ContractFromAPI,
} from '../../services/api';

interface FinancialRiskComparisonModalProps {
  finding: FinancialRiskFinding;
  sourceContractId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const FinancialRiskComparisonModal: React.FC<FinancialRiskComparisonModalProps> = ({
  finding,
  sourceContractId,
  isOpen,
  onClose,
}) => {
  const [contracts, setContracts] = useState<ContractFromAPI[]>([]);
  const [targetContractId, setTargetContractId] = useState<string>('');
  const [suggestions, setSuggestions] = useState<ComparisonSuggestion[]>([]);
  const [acceptedIds, setAcceptedIds] = useState<Set<string>>(new Set());
  const [rejectedIds, setRejectedIds] = useState<Set<string>>(new Set());
  const [isLoadingContracts, setIsLoadingContracts] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load contracts on mount
  useEffect(() => {
    if (!isOpen) return;
    const loadContracts = async () => {
      setIsLoadingContracts(true);
      try {
        const result = await fetchContracts();
        setContracts(result.contracts.filter((c: ContractFromAPI) => c.id !== sourceContractId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load contracts');
      } finally {
        setIsLoadingContracts(false);
      }
    };
    loadContracts();
  }, [isOpen, sourceContractId]);

  // Fetch suggestions when target contract is selected
  const handleTargetSelect = async (contractId: string) => {
    setTargetContractId(contractId);
    setSuggestions([]);
    setAcceptedIds(new Set());
    setRejectedIds(new Set());
    setSessionId(null);
    setError(null);

    if (!contractId) return;

    setIsLoadingSuggestions(true);
    try {
      const result = await fetchComparisonSuggestions(
        finding.clauseText,
        contractId
      );
      setSuggestions(result.suggestions);

      // Create comparison session
      const session = await createComparisonSession(
        finding.id,
        finding.clauseId,
        sourceContractId,
        contractId,
        result.suggestions
      );
      setSessionId(session.sessionId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get suggestions');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const handleAccept = async (clauseId: string) => {
    const newAccepted = new Set(acceptedIds);
    newAccepted.add(clauseId);
    const newRejected = new Set(rejectedIds);
    newRejected.delete(clauseId);
    setAcceptedIds(newAccepted);
    setRejectedIds(newRejected);

    if (sessionId) {
      try {
        await updateComparisonSession(sessionId, {
          acceptedClauseIds: Array.from(newAccepted),
          rejectedClauseIds: Array.from(newRejected),
        });
      } catch {
        // Non-critical; session will be stale but UI state is accurate
      }
    }
  };

  const handleReject = async (clauseId: string) => {
    const newRejected = new Set(rejectedIds);
    newRejected.add(clauseId);
    const newAccepted = new Set(acceptedIds);
    newAccepted.delete(clauseId);
    setRejectedIds(newRejected);
    setAcceptedIds(newAccepted);

    if (sessionId) {
      try {
        await updateComparisonSession(sessionId, {
          acceptedClauseIds: Array.from(newAccepted),
          rejectedClauseIds: Array.from(newRejected),
        });
      } catch {
        // Non-critical
      }
    }
  };

  if (!isOpen) return null;

  const acceptedSuggestions = suggestions.filter((s) =>
    acceptedIds.has(s.targetClauseId)
  );

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      data-testid="comparison-modal"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative min-h-screen flex items-start justify-center p-4 pt-16">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Compare with Another Contract
            </h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="close-modal"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Contract picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Contract
              </label>
              {isLoadingContracts ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading contracts...
                </div>
              ) : (
                <select
                  value={targetContractId}
                  onChange={(e) => handleTargetSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                  data-testid="contract-picker"
                >
                  <option value="">Select a contract to compare...</option>
                  {contracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.state})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Side-by-side: source (left) + suggestions (right) */}
            {targetContractId && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Source clause */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Source (Flagged Clause)
                  </h3>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="text-xs text-orange-600 mb-1 font-medium">
                      Art. {finding.articleNumber} / Sec. {finding.sectionNumber}
                      {finding.sectionTitle && ` - ${finding.sectionTitle}`}
                    </div>
                    <p className="text-sm text-gray-800 leading-relaxed">
                      {finding.clauseText}
                    </p>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      {finding.rationale}
                    </p>
                  </div>

                  {/* Accepted targets shown below source */}
                  {acceptedSuggestions.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-xs font-medium text-green-700 uppercase tracking-wide mb-2">
                        Accepted Matches ({acceptedSuggestions.length})
                      </h4>
                      <div className="space-y-2">
                        {acceptedSuggestions.map((s) => (
                          <div
                            key={s.targetClauseId}
                            className="bg-green-50 border border-green-200 rounded-lg p-3"
                          >
                            <div className="text-xs text-green-600 mb-1">
                              Art. {s.targetArticleNumber} / Sec. {s.targetSectionNumber}
                              {s.targetSectionTitle && ` - ${s.targetSectionTitle}`}
                            </div>
                            <p className="text-sm text-gray-800">
                              {s.targetClauseText.slice(0, 300)}
                              {s.targetClauseText.length > 300 && '...'}
                            </p>
                            <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                              {(s.similarity * 100).toFixed(0)}% match
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Target suggestions */}
                <div>
                  <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Suggested Matches
                  </h3>
                  {isLoadingSuggestions ? (
                    <div className="flex items-center justify-center py-8 text-gray-500">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Finding similar clauses...
                    </div>
                  ) : suggestions.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No similar clauses found in the target contract.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {suggestions.map((s) => {
                        const isAccepted = acceptedIds.has(s.targetClauseId);
                        const isRejected = rejectedIds.has(s.targetClauseId);
                        return (
                          <div
                            key={s.targetClauseId}
                            className={`border rounded-lg p-3 transition-colors ${
                              isAccepted
                                ? 'border-green-300 bg-green-50'
                                : isRejected
                                ? 'border-red-200 bg-red-50 opacity-60'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`suggestion-card-${s.targetClauseId}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">
                                Art. {s.targetArticleNumber} / Sec. {s.targetSectionNumber}
                                {s.targetSectionTitle &&
                                  ` - ${s.targetSectionTitle}`}
                              </span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                {(s.similarity * 100).toFixed(0)}%
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 mb-2">
                              {s.targetClauseText.slice(0, 200)}
                              {s.targetClauseText.length > 200 && '...'}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleAccept(s.targetClauseId)}
                                disabled={isAccepted}
                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  isAccepted
                                    ? 'bg-green-200 text-green-800'
                                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                                }`}
                              >
                                <Check className="h-3 w-3 mr-1" />
                                {isAccepted ? 'Accepted' : 'Accept'}
                              </button>
                              <button
                                onClick={() => handleReject(s.targetClauseId)}
                                disabled={isRejected}
                                className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium transition-colors ${
                                  isRejected
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-red-50 text-red-700 hover:bg-red-100'
                                }`}
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                {isRejected ? 'Rejected' : 'Reject'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
